// apps/office/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import OfficeLayout from './layouts/OfficeLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import OfficeLogin from './pages/OfficeLogin';

// ---- Office Pages ----
import Dashboard from './pages/office/Dashboard';
import Clients from './pages/office/Clients';
import ClientDetail from './pages/office/ClientDetail';
import ClientForm from './pages/office/ClientForm';
import ClientPayment from './pages/office/ClientPayment';
import Quotations from './pages/office/Quotations';
import QuotationForm from './pages/office/QuotationForm';
import QuotationDetail from './pages/office/QuotationDetail';
import ApprovalDashboard from './pages/office/ApprovalDashboard';
import InvoicesList from './pages/office/InvoicesList';
import InvoiceForm from './pages/office/InvoiceForm';
import InvoiceDetail from './pages/office/InvoiceDetail';
import PaymentsDashboard from './pages/office/PaymentsDashboard';
import Tasks from './pages/office/Tasks';
import WhatsAppManager from './pages/office/WhatsAppManager';
import WhatsAppSettings from './pages/office/WhatsAppSettings';

// ---- Interior Projects (new) ----
import InteriorClients from './pages/office/InteriorClients';
import InteriorClientProjects from './pages/office/InteriorClientProjects';
import InteriorProducts from './pages/office/InteriorProducts';
import InteriorQuotationList from './pages/office/InteriorQuotationList';
import InteriorQuotationForm from './pages/office/InteriorQuotationForm';
import InteriorQuotationView from './pages/office/InteriorQuotationView';
import InteriorInvoiceView from './pages/office/InteriorInvoiceView';
import InteriorInvoiceForm from './pages/office/InteriorInvoiceForm';

// ---- Property Pages ----
import Properties from './pages/office/Properties';
import PropertyEnquiries from './pages/office/PropertyEnquiries';

// ---- Other Pages ----
import AbandonedCheckouts from './pages/office/AbandonedCheckouts';
import Reports from './pages/office/Reports';
import PriceList from './pages/office/PriceList';
import PlanningPricePage from './pages/office/PlanningPricePage';
import CartOverview from './pages/office/CartOverview';
import ContactInquiries from './pages/office/ContactInquiries';

// ---- Project pages ----
import Projects from './pages/office/Projects';
import ProjectCreate from './pages/office/ProjectCreate';
import ProjectDetail from './pages/office/ProjectDetail';
import ExpenseDashboard from './pages/office/ExpenseDashboard';
import ProfitabilityDashboard from './pages/office/ProfitabilityDashboard';

// ---- Constants ----
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// ---- App Routes Component ----
const AppRoutes = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<OfficeLogin />} />

      {/* Protected Office Routes */}
      <Route
        path="/office"
        element={
          <ProtectedRoute>
            <OfficeLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />

        {/* Dashboard */}
        <Route path="dashboard" element={<Dashboard />} />

        {/* Clients */}
        <Route path="clients" element={<Clients />} />
        <Route path="clients/new" element={<ClientForm />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="clients/:clientId/payment" element={<ClientPayment />} />

        {/* Projects (architecture) */}
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="clients/:leadId/project/new" element={<ProjectCreate />} />

        {/* Quotations (architecture) */}
        <Route path="quotations" element={<Quotations />} />
        <Route path="quotation/new" element={<QuotationForm />} />
        <Route path="quotation/new/:clientId" element={<QuotationForm />} />
        <Route path="quotation/:id" element={<QuotationDetail />} />
        <Route path="quotation/edit/:quoteId" element={<QuotationForm />} />

        {/* Invoices */}
        <Route path="invoices" element={<InvoicesList />} />
        <Route path="invoices/new" element={<InvoiceForm />} />
        <Route path="invoices/new/:quotationId" element={<InvoiceForm />} />
        <Route path="invoices/edit/:invoiceId" element={<InvoiceForm />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />

        {/* ═══════════════════════════════════════════════
            Interior Projects (new)
            ═══════════════════════════════════════════════ */}
        <Route path="interior-projects" element={<InteriorClients />} />
        <Route
          path="interior-projects/client/:clientId"
          element={<InteriorClientProjects />}
        />
        <Route path="interior-products" element={<InteriorProducts />} />
        <Route path="interior-quotations/project/:projectId" element={<InteriorQuotationList />} />
        <Route path="interior-quotations/new/:projectId" element={<InteriorQuotationForm />} />
        <Route path="interior-quotations/edit/:quoteId" element={<InteriorQuotationForm />} />
        <Route path="interior-quotations/view/:id" element={<InteriorQuotationView />} />
        <Route path="interior-invoices/view/:quotationId" element={<InteriorInvoiceView />} />
        <Route path="interior-invoices/new/:quotationId" element={<InteriorInvoiceForm />} />

        {/* Payments */}
        <Route path="payments-dashboard" element={<PaymentsDashboard />} />
        <Route path="expenses" element={<ExpenseDashboard />} />
        <Route path="profitability" element={<ProfitabilityDashboard />} />

        {/* Tasks */}
        <Route path="tasks" element={<Tasks />} />

        {/* WhatsApp */}
        <Route path="whatsapp" element={<WhatsAppManager />} />
        <Route path="whatsapp/settings" element={<WhatsAppSettings />} />

        {/* Properties */}
        <Route path="properties" element={<Properties />} />
        <Route path="property-enquiries" element={<PropertyEnquiries />} />
        <Route path="contact-inquiries" element={<ContactInquiries />} />

        {/* Cart & Abandoned */}
        <Route path="cart-overview" element={<CartOverview />} />
        <Route path="abandoned" element={<AbandonedCheckouts />} />

        {/* Reports */}
        <Route path="reports" element={<Reports />} />

        {/* Price List */}
        <Route path="price-list" element={<PriceList />} />
        <Route path="planning-prices" element={<PlanningPricePage />} />

        {/* Approvals */}
        <Route path="approvals" element={<ApprovalDashboard />} />

        {/* Catch-all */}
        <Route
          path="*"
          element={
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-700 mb-2">404</h2>
                <p className="text-slate-500 mb-4">Page Not Found</p>
                <p className="text-sm text-slate-400">
                  The page you're looking for doesn't exist or has been moved.
                </p>
              </div>
            </div>
          }
        />
      </Route>

      {/* Root redirects */}
      <Route path="/" element={<Navigate to="/office/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// ---- Main App Component ----
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename="/">
          <AppRoutes />
        </BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            duration: 4000,
            style: {
              fontSize: '14px',
              borderRadius: '12px',
            },
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
